import React, { useState } from 'react';
import { 
  Monitor,
  Activity,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Clock,
  Server,
  Database,
  Cpu,
  HardDrive,
  Wifi,
  Users,
  Eye,
  Trash2,
  Download,
  RefreshCw,
  Settings,
  BarChart3,
  TrendingUp,
  TrendingDown,
  Zap,
  Shield,
  Globe,
  FileText
} from 'lucide-react';
import DataTable from '../common/DataTable';
import ConfirmationModal from '../common/ConfirmationModal';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';

const SystemMonitoring = () => {
  const [activeTab, setActiveTab] = useState('system-health');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  // Mock data for system health
  const [systemHealth, setSystemHealth] = useState([
    {
      id: 'SH-001',
      serviceName: 'Web Server',
      status: 'Healthy',
      uptime: '99.9%',
      responseTime: '45ms',
      cpuUsage: 23.5,
      memoryUsage: 67.2,
      diskUsage: 45.8,
      lastCheck: '2024-02-15 14:30:00',
      location: 'Primary Data Center',
      version: '2.4.1',
      isActive: true,
      tags: ['web', 'critical', 'production'],
      isBookmarked: true,
      isStarred: false,
      viewCount: 156,
      downloadCount: 12,
      aiInsights: ['Performance within normal range', 'Memory usage trending up', 'Consider scaling if growth continues'],
      keyMetrics: {
        requestsPerSecond: 1250,
        errorRate: '0.02%',
        averageResponseTime: '45ms',
        activeConnections: 342
      },
      alerts: ['Memory usage above 60%'],
      performanceScore: 92.5
    },
    {
      id: 'SH-002',
      serviceName: 'Database Server',
      status: 'Warning',
      uptime: '99.7%',
      responseTime: '120ms',
      cpuUsage: 78.3,
      memoryUsage: 89.1,
      diskUsage: 82.4,
      lastCheck: '2024-02-15 14:30:00',
      location: 'Primary Data Center',
      version: '13.2',
      isActive: true,
      tags: ['database', 'critical', 'high-load'],
      isBookmarked: true,
      isStarred: true,
      viewCount: 203,
      downloadCount: 18,
      aiInsights: ['High CPU and memory usage detected', 'Consider optimization or scaling', 'Disk space approaching limit'],
      keyMetrics: {
        queriesPerSecond: 850,
        connectionPool: '85%',
        averageQueryTime: '120ms',
        activeConnections: 156
      },
      alerts: ['High CPU usage', 'Memory usage critical', 'Disk space warning'],
      performanceScore: 65.8
    },
    {
      id: 'SH-003',
      serviceName: 'API Gateway',
      status: 'Healthy',
      uptime: '99.8%',
      responseTime: '28ms',
      cpuUsage: 15.2,
      memoryUsage: 34.7,
      diskUsage: 12.3,
      lastCheck: '2024-02-15 14:30:00',
      location: 'Secondary Data Center',
      version: '3.1.0',
      isActive: true,
      tags: ['api', 'gateway', 'load-balancer'],
      isBookmarked: false,
      isStarred: false,
      viewCount: 89,
      downloadCount: 7,
      aiInsights: ['Excellent performance metrics', 'Low resource utilization', 'Stable operation'],
      keyMetrics: {
        requestsPerSecond: 2100,
        errorRate: '0.01%',
        averageResponseTime: '28ms',
        activeConnections: 89
      },
      alerts: [],
      performanceScore: 96.2
    }
  ]);

  // Mock data for system alerts
  const [systemAlerts, setSystemAlerts] = useState([
    {
      id: 'ALERT-001',
      alertType: 'Critical',
      title: 'Database CPU Usage Critical',
      description: 'Database server CPU usage has exceeded 80% for the last 15 minutes',
      service: 'Database Server',
      severity: 'Critical',
      status: 'Active',
      timestamp: '2024-02-15 14:25:00',
      acknowledged: false,
      resolved: false,
      assignedTo: 'System Admin',
      createdBy: 'Monitoring System',
      isActive: true,
      tags: ['database', 'cpu', 'critical'],
      isBookmarked: false,
      isStarred: true,
      viewCount: 45,
      downloadCount: 3,
      aiInsights: ['Immediate attention required', 'Consider scaling or optimization', 'Check for runaway queries'],
      resolutionSteps: ['Check active queries', 'Review resource allocation', 'Consider scaling'],
      estimatedImpact: 'High - May affect application performance'
    },
    {
      id: 'ALERT-002',
      alertType: 'Warning',
      title: 'Disk Space Warning',
      description: 'Disk usage on web server has reached 85% capacity',
      service: 'Web Server',
      severity: 'Warning',
      status: 'Acknowledged',
      timestamp: '2024-02-15 13:45:00',
      acknowledged: true,
      resolved: false,
      assignedTo: 'DevOps Team',
      createdBy: 'Monitoring System',
      isActive: true,
      tags: ['storage', 'warning', 'cleanup-needed'],
      isBookmarked: true,
      isStarred: false,
      viewCount: 23,
      downloadCount: 2,
      aiInsights: ['Storage cleanup recommended', 'Monitor growth rate', 'Consider archiving old logs'],
      resolutionSteps: ['Clean up temporary files', 'Archive old logs', 'Review storage allocation'],
      estimatedImpact: 'Medium - May affect log rotation'
    }
  ]);

  // Calculate statistics
  const stats = {
    totalServices: systemHealth.length,
    healthyServices: systemHealth.filter(s => s.status === 'Healthy').length,
    warningServices: systemHealth.filter(s => s.status === 'Warning').length,
    criticalServices: systemHealth.filter(s => s.status === 'Critical').length,
    averageUptime: systemHealth.reduce((sum, service) => {
      const uptime = parseFloat(service.uptime.replace('%', ''));
      return sum + uptime;
    }, 0) / systemHealth.length,
    totalAlerts: systemAlerts.length,
    activeAlerts: systemAlerts.filter(a => a.status === 'Active').length,
    criticalAlerts: systemAlerts.filter(a => a.severity === 'Critical').length,
    averageResponseTime: systemHealth.reduce((sum, service) => {
      const responseTime = parseFloat(service.responseTime.replace('ms', ''));
      return sum + responseTime;
    }, 0) / systemHealth.length
  };

  const tabs = [
    { id: 'system-health', label: 'System Health' },
    { id: 'alerts', label: 'Alerts & Notifications' },
    { id: 'performance', label: 'Performance Metrics' },
    { id: 'logs', label: 'System Logs' }
  ];

  // Define columns for System Health DataTable
  const systemHealthColumns = [
    {
      key: 'serviceName',
      label: 'Service Details',
      width: '250px',
      render: (value, row) => (
        <div>
          <div style={{ fontWeight: '500', color: '#1f2937', marginBottom: '4px' }}>{value}</div>
          <div style={{ fontSize: '12px', color: '#6b7280' }}>
            {row.location} • v{row.version}
          </div>
        </div>
      )
    },
    {
      key: 'status',
      label: 'Status',
      width: '100px',
      render: (status) => {
        const statusConfig = {
          'Healthy': { color: '#10b981', bg: '#d1fae5', icon: <CheckCircle size={14} /> },
          'Warning': { color: '#f59e0b', bg: '#fef3c7', icon: <AlertTriangle size={14} /> },
          'Critical': { color: '#ef4444', bg: '#fee2e2', icon: <XCircle size={14} /> },
          'Offline': { color: '#6b7280', bg: '#f3f4f6', icon: <XCircle size={14} /> }
        };
        const config = statusConfig[status] || statusConfig['Healthy'];
        
        return (
          <span style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '6px 10px',
            borderRadius: '6px',
            fontSize: '12px',
            fontWeight: '500',
            backgroundColor: config.bg,
            color: config.color,
            width: 'fit-content'
          }}>
            {config.icon}
            {status}
          </span>
        );
      }
    },
    {
      key: 'uptime',
      label: 'Uptime',
      width: '100px',
      render: (uptime) => (
        <span style={{ fontWeight: '600', color: '#10b981' }}>{uptime}</span>
      )
    },
    {
      key: 'responseTime',
      label: 'Response Time',
      width: '120px',
      render: (time) => (
        <span style={{ fontWeight: '500', color: '#1f2937' }}>{time}</span>
      )
    },
    {
      key: 'cpuUsage',
      label: 'CPU Usage',
      width: '120px',
      render: (usage) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{
            width: '60px',
            height: '6px',
            backgroundColor: '#e5e7eb',
            borderRadius: '3px',
            overflow: 'hidden'
          }}>
            <div style={{
              width: `${Math.min(usage, 100)}%`,
              height: '100%',
              backgroundColor: usage >= 80 ? '#ef4444' : usage >= 60 ? '#f59e0b' : '#10b981',
              transition: 'width 0.3s ease'
            }} />
          </div>
          <span style={{ 
            fontSize: '12px', 
            fontWeight: '500', 
            color: usage >= 80 ? '#ef4444' : usage >= 60 ? '#f59e0b' : '#10b981' 
          }}>
            {usage.toFixed(1)}%
          </span>
        </div>
      )
    },
    {
      key: 'memoryUsage',
      label: 'Memory Usage',
      width: '120px',
      render: (usage) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{
            width: '60px',
            height: '6px',
            backgroundColor: '#e5e7eb',
            borderRadius: '3px',
            overflow: 'hidden'
          }}>
            <div style={{
              width: `${Math.min(usage, 100)}%`,
              height: '100%',
              backgroundColor: usage >= 80 ? '#ef4444' : usage >= 60 ? '#f59e0b' : '#10b981',
              transition: 'width 0.3s ease'
            }} />
          </div>
          <span style={{ 
            fontSize: '12px', 
            fontWeight: '500', 
            color: usage >= 80 ? '#ef4444' : usage >= 60 ? '#f59e0b' : '#10b981' 
          }}>
            {usage.toFixed(1)}%
          </span>
        </div>
      )
    },
    {
      key: 'performanceScore',
      label: 'Performance',
      width: '120px',
      render: (score) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{
            width: '60px',
            height: '6px',
            backgroundColor: '#e5e7eb',
            borderRadius: '3px',
            overflow: 'hidden'
          }}>
            <div style={{
              width: `${score}%`,
              height: '100%',
              backgroundColor: score >= 90 ? '#10b981' : score >= 70 ? '#f59e0b' : '#ef4444',
              transition: 'width 0.3s ease'
            }} />
          </div>
          <span style={{ 
            fontSize: '12px', 
            fontWeight: '500', 
            color: score >= 90 ? '#10b981' : score >= 70 ? '#f59e0b' : '#ef4444' 
          }}>
            {score.toFixed(1)}
          </span>
        </div>
      )
    },
    {
      key: 'actions',
      label: 'Actions',
      width: '200px',
      render: (value, row) => (
        <div style={{ display: 'flex', gap: '6px' }}>
          <button
            onClick={() => handleView(row)}
            style={{
              padding: '6px 10px',
              fontSize: '12px',
              borderRadius: '6px',
              borderColor: 'var(--primary-color)',
              color: '#007B8F',
              backgroundColor: 'transparent',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}
            title="View Details"
          >
            <Eye size={14} /> View
          </button>
          <button
            onClick={() => handleExport(row)}
            style={{
              padding: '6px 10px',
              fontSize: '12px',
              borderRadius: '6px',
              borderColor: '#10b981',
              color: '#10b981',
              backgroundColor: 'transparent',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}
            title="Export Report"
          >
            <Download size={14} /> Export
          </button>
          <button
            onClick={() => handleDelete(row)}
            style={{
              padding: '6px 10px',
              fontSize: '12px',
              borderRadius: '6px',
              borderColor: '#ef4444',
              color: '#ef4444',
              backgroundColor: 'transparent',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}
            title="Delete Service"
          >
            <Trash2 size={14} /> Delete
          </button>
        </div>
      )
    }
  ];

  // Define columns for Alerts DataTable
  const alertsColumns = [
    {
      key: 'title',
      label: 'Alert Details',
      width: '300px',
      render: (value, row) => (
        <div>
          <div style={{ fontWeight: '500', color: '#1f2937', marginBottom: '4px' }}>{value}</div>
          <div style={{ fontSize: '12px', color: '#6b7280' }}>
            {row.service} • {row.timestamp}
          </div>
        </div>
      )
    },
    {
      key: 'severity',
      label: 'Severity',
      width: '100px',
      render: (severity) => {
        const severityConfig = {
          'Critical': { color: '#ef4444', bg: '#fee2e2' },
          'Warning': { color: '#f59e0b', bg: '#fef3c7' },
          'Info': { color: '#3b82f6', bg: '#dbeafe' },
          'Low': { color: '#10b981', bg: '#d1fae5' }
        };
        const config = severityConfig[severity] || severityConfig['Info'];
        
        return (
          <span style={{
            padding: '4px 8px',
            borderRadius: '4px',
            fontSize: '12px',
            fontWeight: '500',
            backgroundColor: config.bg,
            color: config.color
          }}>
            {severity}
          </span>
        );
      }
    },
    {
      key: 'status',
      label: 'Status',
      width: '120px',
      render: (status) => {
        const statusConfig = {
          'Active': { color: '#ef4444', bg: '#fee2e2', icon: <AlertTriangle size={14} /> },
          'Acknowledged': { color: '#f59e0b', bg: '#fef3c7', icon: <Clock size={14} /> },
          'Resolved': { color: '#10b981', bg: '#d1fae5', icon: <CheckCircle size={14} /> }
        };
        const config = statusConfig[status] || statusConfig['Active'];
        
        return (
          <span style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '6px 10px',
            borderRadius: '6px',
            fontSize: '12px',
            fontWeight: '500',
            backgroundColor: config.bg,
            color: config.color,
            width: 'fit-content'
          }}>
            {config.icon}
            {status}
          </span>
        );
      }
    },
    {
      key: 'assignedTo',
      label: 'Assigned To',
      width: '120px',
      render: (assigned) => (
        <span style={{ fontWeight: '500', color: '#1f2937' }}>{assigned}</span>
      )
    },
    {
      key: 'estimatedImpact',
      label: 'Impact',
      width: '150px',
      render: (impact) => (
        <span style={{ fontSize: '12px', color: '#6b7280' }}>{impact}</span>
      )
    },
    {
      key: 'actions',
      label: 'Actions',
      width: '200px',
      render: (value, row) => (
        <div style={{ display: 'flex', gap: '6px' }}>
          <button
            onClick={() => handleView(row)}
            style={{
              padding: '6px 10px',
              fontSize: '12px',
              borderRadius: '6px',
              borderColor: 'var(--primary-color)',
              color: '#007B8F',
              backgroundColor: 'transparent',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}
            title="View Details"
          >
            <Eye size={14} /> View
          </button>
          <button
            onClick={() => handleExport(row)}
            style={{
              padding: '6px 10px',
              fontSize: '12px',
              borderRadius: '6px',
              borderColor: '#10b981',
              color: '#10b981',
              backgroundColor: 'transparent',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}
            title="Export Alert"
          >
            <Download size={14} /> Export
          </button>
          <button
            onClick={() => handleDelete(row)}
            style={{
              padding: '6px 10px',
              fontSize: '12px',
              borderRadius: '6px',
              borderColor: '#ef4444',
              color: '#ef4444',
              backgroundColor: 'transparent',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}
            title="Delete Alert"
          >
            <Trash2 size={14} /> Delete
          </button>
        </div>
      )
    }
  ];

  // Handler functions
  const handleView = (item) => {
    //console.log('Viewing item:', item.serviceName || item.title);
  };

  const handleExport = (item) => {
    setSelectedItem(item);
    setShowExportModal(true);
  };

  const handleDelete = (item) => {
    setSelectedItem(item);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    setIsDeleting(true);
    try {
      if (activeTab === 'system-health') {
        setSystemHealth(prev => prev.filter(service => service.id !== selectedItem.id));
      } else {
        setSystemAlerts(prev => prev.filter(alert => alert.id !== selectedItem.id));
      }
      setShowDeleteModal(false);
      setSelectedItem(null);
    } catch (error) {
      console.error('Error deleting item:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleExportConfirm = async () => {
    setIsProcessing(true);
    try {
      setShowExportModal(false);
      setSelectedItem(null);
    } catch (error) {
      console.error('Error exporting report:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <>
      <PageBreadcrumb />
      <PageHeader 
        title="System Monitoring"
        description="Real-time system health monitoring, performance metrics, and alert management"
      />
      <div className="page-container">

      {/* Tabbed Navigation */}
      <div style={{ marginBottom: '32px' }}>
        <div style={{
          display: 'flex',
          borderBottom: '1px solid #e5e7eb',
          gap: '0'
        }}>
          {tabs.map(tab => (
            <button
              key={tab.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '12px 24px',
                color: activeTab === tab.id ? 'var(--primary-color)' : 'var(--text-muted)',
                borderBottom: activeTab === tab.id ? `3px solid var(--primary-color)` : '3px solid transparent',
                transition: 'all 0.15s ease-in-out',
                fontWeight: '500',
                fontSize: '14px',
                backgroundColor: 'transparent',
                border: 'none',
                cursor: 'pointer',
                borderRadius: '6px 6px 0 0',
                fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
                position: 'relative',
                marginBottom: '-1px'
              }}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.id === 'system-health' && <Monitor size={18} style={{ color: activeTab === tab.id ? 'var(--primary-color)' : '#9ca3af' }} />}
              {tab.id === 'alerts' && <AlertTriangle size={18} style={{ color: activeTab === tab.id ? 'var(--primary-color)' : '#9ca3af' }} />}
              {tab.id === 'performance' && <BarChart3 size={18} style={{ color: activeTab === tab.id ? 'var(--primary-color)' : '#9ca3af' }} />}
              {tab.id === 'logs' && <FileText size={18} style={{ color: activeTab === tab.id ? 'var(--primary-color)' : '#9ca3af' }} />}
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Stats Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        gap: '24px',
        marginBottom: '32px'
      }}>
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '20px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          border: '1px solid #e5e7eb'
        }}>
          <div>
            <p style={{ fontSize: '14px', color: '#6b7280', margin: '0 0 8px 0' }}>Total Services</p>
            <h2 style={{ fontSize: '28px', fontWeight: '700', color: '#1f2937', margin: 0 }}>{stats.totalServices}</h2>
            <p style={{ fontSize: '12px', color: '#6b7280', margin: '4px 0 0 0' }}>
              {stats.healthyServices} healthy, {stats.warningServices} warning
            </p>
          </div>
          <div style={{
            width: '48px',
            height: '48px',
            borderRadius: '12px',
            backgroundColor: 'var(--primary-light)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--primary-color)'
          }}>
            <Server size={20} />
          </div>
        </div>

        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '20px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          border: '1px solid #e5e7eb'
        }}>
          <div>
            <p style={{ fontSize: '14px', color: '#6b7280', margin: '0 0 8px 0' }}>Average Uptime</p>
            <h2 style={{ fontSize: '28px', fontWeight: '700', color: '#1f2937', margin: 0 }}>
              {stats.averageUptime.toFixed(1)}%
            </h2>
            <p style={{ fontSize: '12px', color: '#6b7280', margin: '4px 0 0 0' }}>
              System availability
            </p>
          </div>
          <div style={{
            width: '48px',
            height: '48px',
            borderRadius: '12px',
            backgroundColor: '#10b98120',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#10b981'
          }}>
            <Activity size={20} />
          </div>
        </div>

        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '20px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          border: '1px solid #e5e7eb'
        }}>
          <div>
            <p style={{ fontSize: '14px', color: '#6b7280', margin: '0 0 8px 0' }}>Active Alerts</p>
            <h2 style={{ fontSize: '28px', fontWeight: '700', color: '#1f2937', margin: 0 }}>
              {stats.activeAlerts}
            </h2>
            <p style={{ fontSize: '12px', color: '#6b7280', margin: '4px 0 0 0' }}>
              {stats.criticalAlerts} critical alerts
            </p>
          </div>
          <div style={{
            width: '48px',
            height: '48px',
            borderRadius: '12px',
            backgroundColor: '#ef444420',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#ef4444'
          }}>
            <AlertTriangle size={20} />
          </div>
        </div>

        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '20px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          border: '1px solid #e5e7eb'
        }}>
          <div>
            <p style={{ fontSize: '14px', color: '#6b7280', margin: '0 0 8px 0' }}>Avg Response Time</p>
            <h2 style={{ fontSize: '28px', fontWeight: '700', color: '#1f2937', margin: 0 }}>
              {stats.averageResponseTime.toFixed(0)}ms
            </h2>
            <p style={{ fontSize: '12px', color: '#6b7280', margin: '4px 0 0 0' }}>
              System performance
            </p>
          </div>
          <div style={{
            width: '48px',
            height: '48px',
            borderRadius: '12px',
            backgroundColor: '#f59e0b20',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#f59e0b'
          }}>
            <TrendingUp size={20} />
          </div>
        </div>
      </div>

      {/* System Health Tab */}
      {activeTab === 'system-health' && (
        <div>
          <div style={{ overflowX: 'auto', maxWidth: '100%' }}>
            <DataTable
              data={systemHealth}
              columns={systemHealthColumns}
              title={`System Health Monitoring (${systemHealth.length} services)`}
              searchable={false}
              sortable={true}
              exportable={true}
              pagination={true}
              pageSize={10}
              hiddenColumns={['none']}
              onHiddenColumnsChange={() => {}}
            />
          </div>
        </div>
      )}

      {/* Alerts Tab */}
      {activeTab === 'alerts' && (
        <div>
          <div style={{ overflowX: 'auto', maxWidth: '100%' }}>
            <DataTable
              data={systemAlerts}
              columns={alertsColumns}
              title={`System Alerts (${systemAlerts.length} alerts)`}
              searchable={false}
              sortable={true}
              exportable={true}
              pagination={true}
              pageSize={10}
              hiddenColumns={['none']}
              onHiddenColumnsChange={() => {}}
            />
          </div>
        </div>
      )}

      {/* Other Tabs */}
      {activeTab === 'performance' && (
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '20px',
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
          border: '1px solid #e5e7eb',
          minHeight: '300px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '18px',
          color: '#6b7280'
        }}>
          Performance Metrics Dashboard Coming Soon!
        </div>
      )}

      {activeTab === 'logs' && (
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '20px',
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
          border: '1px solid #e5e7eb',
          minHeight: '300px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '18px',
          color: '#6b7280'
        }}>
          System Logs Dashboard Coming Soon!
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteConfirm}
        title="Delete Item"
        message={`Are you sure you want to delete "${selectedItem?.serviceName || selectedItem?.title}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
        isLoading={isDeleting}
      />

      {/* Export Report Modal */}
      <ConfirmationModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        onConfirm={handleExportConfirm}
        title="Export Report"
        message={`Export monitoring report for "${selectedItem?.serviceName || selectedItem?.title}"? This will generate a comprehensive report with system metrics and analysis.`}
        confirmText="Export"
        cancelText="Cancel"
        type="success"
        isLoading={isProcessing}
      />
      </div>
    </>
  );
};

export default SystemMonitoring;
